/**
 * Central IdP (activation-service) — session endpoints under `/auth`.
 *
 * The portal no longer holds a token: `/auth/login` sets an HttpOnly cookie
 * (`ism_portal`, host-only, 8 h) and the edge turns that cookie into the
 * backend `Token` header per request via an nginx `auth_request` to
 * `/internal/portal-resolve`. Nothing here is reachable from JS, so an XSS can
 * no longer lift the session.
 *
 * Same-origin by design — see `shared/lib/env.ts`. In dev the Vite proxy
 * forwards `/auth` to the IdP so the cookie lands on localhost.
 */

export interface IdpEnv {
  vault_id: string;
  env_name: string;
  is_default?: boolean;
  /** The contour this account entered last, as recorded by the IdP. */
  is_last?: boolean;
}

/**
 * The portal user as the environment's `sp_Login_GET_UserInfo` returns it,
 * minus `AccessToken` / `MiddlewareToken` which the IdP strips before the
 * response ever reaches the browser.
 */
export interface PortalUserDto {
  UserID: number;
  UserGUID: string;
  UserName: string | null;
  Login: string;
  DefaultPage: string | null;
  DefaultPageDistr?: string | null;
  DefaultPageClient?: string | null;
  DistrID: number | null;
  ClientID: number | null;
  PortalAccess: string[] | null;
  IsTemporary?: boolean;
  OSMSettings?: string | null;
  UserSettings?: unknown;
  StorageDatabase?: string | null;
  Distributors?: unknown;
}

export type LoginResult =
  /** Single environment — the cookie is already set. */
  | { status: 'ok'; envName: string; user: PortalUserDto }
  /** Several environments — credentials are stashed server-side for 5 min. */
  | { status: 'select_env'; ticket: string; envs: IdpEnv[] };

export interface SessionInfo {
  login: string;
  vaultId: string;
  envName: string;
  userGuid: string;
  role: number | null;
  /**
   * Profile cached at login. Goes stale after a distributor switch, because
   * `SP_Common/setDefaultDistr` mutates the backend token and never tells the
   * IdP — so the authoritative profile is the environment's own `/me`.
   */
  user: PortalUserDto | null;
  envs: IdpEnv[];
}

/** Error codes the IdP returns in `{ error }`; anything else is unexpected. */
export type IdpErrorCode =
  | 'invalid_credentials'
  | 'captcha_failed'
  | 'locked'
  | 'no_access'
  | 'backend_login_failed'
  | 'ticket_expired'
  | 'forbidden_vault'
  | 'reauth_required'
  | 'switch_failed'
  | 'unauthenticated'
  | 'unavailable'
  | 'bad_request';

export class IdpError extends Error {
  status: number;
  code: IdpErrorCode | string;

  constructor(status: number, code: IdpErrorCode | string, message?: string) {
    super(message || code);
    this.name = 'IdpError';
    this.status = status;
    this.code = code;
  }
}

async function idp<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/auth${path}`, {
    ...init,
    // The whole point: the session cookie must ride along, and Set-Cookie on
    // the way back must be honoured.
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }

  if (!res.ok) {
    const code =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `http_${res.status}`;
    const message =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : undefined;
    throw new IdpError(res.status, code, message);
  }

  return body as T;
}

/**
 * `SHA512(password + Salt)`, hex, upper case — the hash `dbo.Users` stores and
 * `sp_Login_GET_UserInfo` compares against. Identical to
 * `portal/src/app/login/login.factory.js`, which uses CryptoJS; Web Crypto
 * needs no dependency but does require a secure context (https, or localhost,
 * which browsers count as trustworthy).
 */
export async function passwordEncode(password: string, salt: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-512',
    new TextEncoder().encode(`${password}${salt}`)
  );
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/**
 * Per-user salt. Always 200: unknown logins get a deterministic fake one, so
 * the response cannot be used to enumerate accounts. A wrong salt simply makes
 * the hash mismatch and `/auth/login` answers `invalid_credentials`.
 */
export async function getSalt(login: string): Promise<string> {
  const res = await idp<{ salt: string }>(`/salt?login=${encodeURIComponent(login)}`);
  return res.salt;
}

export async function login(
  loginName: string,
  password: string,
  turnstileToken?: string | null
): Promise<LoginResult> {
  const salt = await getSalt(loginName);
  const res = await idp<{
    status: 'ok' | 'select_env';
    env_name?: string;
    user?: PortalUserDto;
    ticket?: string;
    envs?: IdpEnv[];
  }>('/login', {
    method: 'POST',
    body: JSON.stringify({
      login: loginName,
      password_encode: await passwordEncode(password, salt),
      turnstileToken: turnstileToken ?? null,
    }),
  });

  if (res.status === 'select_env') {
    return { status: 'select_env', ticket: res.ticket ?? '', envs: res.envs ?? [] };
  }
  return { status: 'ok', envName: res.env_name ?? '', user: res.user as PortalUserDto };
}

/** Finish a multi-environment login. The ticket expires in 5 minutes. */
export async function selectEnv(ticket: string, vaultId: string) {
  return idp<{ status: 'ok'; env_name: string; user: PortalUserDto }>('/select-env', {
    method: 'POST',
    body: JSON.stringify({ ticket, vault_id: vaultId }),
  });
}

/** Current session, or `null` when there is no valid cookie. */
export async function me(): Promise<SessionInfo | null> {
  try {
    const res = await idp<{
      login: string;
      vault_id: string;
      env_name: string;
      user_guid: string;
      role: number | null;
      user: PortalUserDto | null;
      envs: IdpEnv[];
    }>('/me');
    return {
      login: res.login,
      vaultId: res.vault_id,
      envName: res.env_name,
      userGuid: res.user_guid,
      role: res.role,
      user: res.user,
      envs: res.envs ?? [],
    };
  } catch (e) {
    if (e instanceof IdpError && e.status === 401) return null;
    throw e;
  }
}

/**
 * Move the session to another environment without re-entering the password:
 * the IdP replays the stashed hash against the target backend. Throws
 * `reauth_required` (409) when that hash is gone from Valkey.
 */
export async function switchEnv(vaultId: string) {
  return idp<{ status: 'ok'; env_name: string; user: PortalUserDto }>('/switch-env', {
    method: 'POST',
    body: JSON.stringify({ vault_id: vaultId }),
  });
}

export async function logout(): Promise<void> {
  try {
    await idp('/logout', { method: 'POST' });
  } catch {
    // Clearing the cookie is best-effort; the local store is cleared regardless.
  }
}
